/**
 * Sanitize a raw upload error into UI-friendly text.
 *
 * Why: axios, S3, and DOM errors leak implementation detail to the user —
 * `"Request failed with status code 403"`, raw S3 XML, `ENOTFOUND` host
 * traces. The upload tray must show short, actionable copy. Callers are
 * responsible for logging the original `Error` (with stack) via
 * `logger.error` before invoking this — sanitisation only happens at the
 * UI boundary.
 *
 * Buckets (ordered: most specific first):
 *   - network / connectivity failures  → "Network error, please try again"
 *   - HTTP 4xx                         → "Upload rejected by server"
 *   - HTTP 5xx                         → "Server error, please try again later"
 *   - anything else                    → "Upload failed"
 */

import { i18n } from '@/shared/i18n';

/* eslint-disable i18next/no-literal-string -- fragments matched against a raw error message */
const NETWORK_FRAGMENTS: ReadonlyArray<string> = [
  'network error',
  'failed to fetch',
  'enotfound',
  'econnreset',
  'econnrefused',
  'etimedout',
];
/* eslint-enable i18next/no-literal-string */

// The negative lookahead `(?!\d)` is load-bearing: without it,
// `"status code 1234"` captured `123` (over-bucketed) and `"status code
// 4999"` captured `499` (wrong bucket — "Upload rejected by server").
// Anchoring to exactly-3-digit sequences makes 4+ digit garbage fall
// through to the default "Upload failed".
const HTTP_STATUS_PATTERN = /status\s*code\s*(\d{3})(?!\d)/i;

export function sanitizeErrorMessage(raw: string): string {
  const lower = raw.toLowerCase();

  for (const fragment of NETWORK_FRAGMENTS) {
    if (lower.includes(fragment))
      return i18n.t('projects:uploadQueue.errorMessage.networkErrorPleaseTryAgain');
  }

  const statusMatch = HTTP_STATUS_PATTERN.exec(raw);
  if (statusMatch) {
    const status = Number.parseInt(statusMatch[1], 10);
    if (status >= 400 && status < 500)
      return i18n.t('projects:uploadQueue.errorMessage.uploadRejectedByServer');
    if (status >= 500 && status < 600)
      return i18n.t('projects:uploadQueue.errorMessage.serverErrorPleaseTryAgainLater');
  }

  return i18n.t('projects:uploadQueue.errorMessage.uploadFailed');
}
